'use server'

import { apiRequest, ApiResponse } from '../request'

export interface SiteBannerPlacement {
    key: string
    label: string
    description: string
    multiple: boolean
    recommendedRatio: string
}

export interface SiteBanner {
    id: number
    placement: string
    name: string
    imageUrl: string
    imageMobileUrl: string | null
    alt: string | null
    link: string | null
    linkNewTab: boolean
    order: number
    active: boolean
    startsAt: string | null
    endsAt: string | null
    createdAt: string
    updatedAt: string
}

export interface SiteVideo {
    id: number
    placement: string
    provider: 'youtube' | 'mp4'
    videoId: string | null
    url: string | null
    poster: string | null
    title: string | null
    active: boolean
}

export interface SiteBannerPayload {
    placement?: string
    name?: string
    imageUrl?: string
    imageMobileUrl?: string | null
    alt?: string | null
    link?: string | null
    linkNewTab?: boolean
    order?: number
    active?: boolean
    startsAt?: string | null
    endsAt?: string | null
}

export interface SiteVideoPayload {
    provider: 'youtube' | 'mp4'
    videoId?: string
    url?: string
    poster?: string
    title?: string
    active?: boolean
}

export async function getSitePlacements(): Promise<
    ApiResponse<SiteBannerPlacement[]>
> {
    return apiRequest<SiteBannerPlacement[]>('marketing/site-banners/placements', {})
}

export async function getSiteBanners(
    placement?: string,
): Promise<ApiResponse<SiteBanner[]>> {
    return apiRequest<SiteBanner[]>('marketing/site-banners', {
        params: placement ? { placement } : undefined,
    })
}

export async function getSiteBanner(id: number): Promise<ApiResponse<SiteBanner>> {
    return apiRequest<SiteBanner>(`marketing/site-banners/${id}`, {})
}

export async function createSiteBanner(
    data: SiteBannerPayload,
): Promise<ApiResponse<SiteBanner>> {
    return apiRequest<SiteBanner>('marketing/site-banners', { data }, 'post')
}

export async function updateSiteBanner(
    id: number,
    data: SiteBannerPayload,
): Promise<ApiResponse<SiteBanner>> {
    return apiRequest<SiteBanner>(`marketing/site-banners/${id}`, { data }, 'patch')
}

export async function toggleSiteBanner(
    id: number,
): Promise<ApiResponse<SiteBanner>> {
    return apiRequest<SiteBanner>(`marketing/site-banners/${id}/toggle`, {}, 'patch')
}

export async function reorderSiteBanners(
    items: { id: number; order: number }[],
): Promise<ApiResponse<{ updated: number }>> {
    return apiRequest<{ updated: number }>(
        'marketing/site-banners/reorder',
        { data: { items } },
        'patch',
    )
}

export async function deleteSiteBanner(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`marketing/site-banners/${id}`, {}, 'delete')
}

export async function uploadSiteBannerImage(
    file: File,
): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData()
    formData.append('file', file)

    return apiRequest<{ url: string }>(
        'marketing/site-banners/upload',
        { data: formData, formData: true },
        'post',
    )
}

export async function getSiteVideos(): Promise<ApiResponse<SiteVideo[]>> {
    return apiRequest<SiteVideo[]>('marketing/site-banners/videos', {})
}

export async function upsertSiteVideo(
    placement: string,
    data: SiteVideoPayload,
): Promise<ApiResponse<SiteVideo>> {
    return apiRequest<SiteVideo>(`marketing/site-banners/videos/${placement}`, { data }, 'put')
}

export async function deleteSiteVideo(
    placement: string,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`marketing/site-banners/videos/${placement}`, {}, 'delete')
}
